import cn from 'classnames'
import {useCallback, useEffect, useRef} from 'react'
import type {UseFormRegister} from 'react-hook-form'
import type {FormData} from 'src/Event/hooks/useHubspotForm'

type Props = {
  placeholder: string
  register: UseFormRegister<FormData>
  fieldName: string
  requiredMessage: string
  className?: string
  inputClassname?: string
  type?: string
  isRequired?: boolean
  value?: string
  emailValidationMessage?: string
  hasError?: boolean
}

export const Input = ({
  placeholder,
  register,
  fieldName,
  requiredMessage,
  className,
  inputClassname,
  type = 'text',
  isRequired = true,
  value,
  emailValidationMessage,
  hasError,
}: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null)

  const {ref, ...registerProps} = register(fieldName, {
    required: isRequired ? requiredMessage : false,
  })

  // Validate input and set custom validation message
  const validateInput = useCallback(
    (input: HTMLInputElement) => {
      // Check if field is required and empty
      if (isRequired && (!input.value || input.value === '')) {
        input.setCustomValidity(requiredMessage)
        return
      }

      // Check email type mismatch (invalid email format)
      if (type === 'email' && input.value && input.validity.typeMismatch && emailValidationMessage) {
        input.setCustomValidity(emailValidationMessage)
        return
      }

      // Clear custom validation message if everything is valid
      input.setCustomValidity('')
    },
    [isRequired, requiredMessage, type, emailValidationMessage],
  )

  // Validate on value change
  useEffect(() => {
    if (inputRef.current) {
      validateInput(inputRef.current)
    }
  }, [value, isRequired, requiredMessage, emailValidationMessage, validateInput])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (inputRef.current) {
      validateInput(inputRef.current)
    }

    registerProps.onChange(e)
  }

  // Handle browser validation event - set custom message before browser shows it
  const handleInvalid = () => {
    if (inputRef.current) {
      validateInput(inputRef.current)
    }
  }

  const displayPlaceholder = isRequired ? `${placeholder} *` : placeholder

  return (
    <label className={className}>
      <input
        className={cn(
          'h-10 w-full rounded border bg-moss-white px-3 py-1.5 text-14 leading-5 outline-none transition-shadow placeholder:text-moss-grey-300 focus:shadow-card',
          hasError ? 'border-ui-red-450' : 'border-moss-black/10',
          inputClassname,
        )}
        placeholder={displayPlaceholder}
        type={type}
        {...registerProps}
        ref={(e) => {
          ref(e)
          inputRef.current = e
        }}
        onChange={handleChange}
        onInvalid={handleInvalid}
      />
    </label>
  )
}
